import { useRef } from 'react';
import { Navigate, Outlet } from 'react-router';

import { useAuth } from '../hooks/useAuth';
import { useWorkspace } from '../hooks/useWorkspace';

/**
 * The sign-in and sign-up forms, for whoever still needs them.
 *
 * A merchant who is already signed in is not asked to do it again: arriving at
 * either form with a session in hand sends them on to where that session works.
 * Without this, a back button, a bookmark or the footer's sitemap lands a
 * signed-in merchant on "Create your account" - which reads as though the
 * account they just made did not take.
 *
 * Only a session that existed *before* the form mounted counts. Signing in or
 * registering also turns `status` authenticated, and those pages navigate
 * themselves afterwards with far more context than this has: which page sent
 * the caller to `/login`, which plan is waiting, whether the business was
 * created. Redirecting on that transition would overrule all of it - and on
 * `/register` it would cut in before the business is created and strand the new
 * merchant in onboarding they had already been carried through.
 *
 * While the boot refresh is still deciding, the form shows. Almost nobody who
 * loads `/login` cold turns out to have a session waiting, and holding the form
 * behind that round trip would make all of them wait on an answer they are not
 * waiting for. One who does is moved on the moment it arrives.
 */
export function SignedOutOnly() {
  const { status } = useAuth();
  const workspace = useWorkspace();

  // Latched, not derived: once this browser has shown itself to be signed out,
  // any session that appears afterwards was made on the form below.
  const wasAnonymous = useRef(false);
  if (status === 'anonymous') wasAnonymous.current = true;

  if (status === 'authenticated' && !wasAnonymous.current) {
    return <Navigate to={workspace.to} replace />;
  }

  return <Outlet />;
}
